import { cleanCenterDisplayName } from "@/lib/text/center-name";
import physicianDisplayOverridesRaw from "@/lib/text/physician-display-overrides.json";
import { cleanPhysicianName } from "./url-builder";

/**
 * Liste blanche d'affichage des noms de médecins — DISPLAY ONLY.
 *
 * 1 328 fiches portent un nom farci de mots-clés que cleanPhysicianName ne
 * rattrape pas (il ne strippe qu'en TÊTE de chaîne) : « Dr Amine DENTISTE À
 * TEMARA YAAKOUBI », « Dr ORL MUSTAPHA MEZAHI », « Dr de Médecine générale
 * HATIM CHARFI ». Une règle heuristique a été testée hors ligne sur les
 * 42 648 fiches hors centres : 25 % de ses sorties étaient CASSÉES (token
 * doublé « KHADIRI KHADIRI », bruit résiduel « générale HATIM CHARFI »,
 * apostrophe orpheline), donc pas de règle générique en production — un nom
 * cassé dans le <title>, le H1 et le JSON-LD d'une fiche YMYL est pire que le
 * nom farci qu'il remplace.
 *
 * Ce fichier est donc une liste RELUE À LA MAIN, dans la même logique que
 * cleanCenterDisplayName (295 centres) : les 192 fiches polluées qui portent
 * du trafic ont été reprises une par une, 158 corrigées, 34 volontairement
 * laissées telles quelles (raisons sociales mal classées en médecin — « Best
 * Smile Clinic », « Labo d'analyses Assounna » — qui relèvent d'un
 * reclassement en centre, et résidus ambigus). Couverture : 11 380
 * impressions / 372 clics sur 28 j (GSC, 2026-08-23→09-19). Aucune fiche
 * revendiquée ni GOLD dans le lot (mesure prod : 0).
 *
 * N'AFFECTE PAS LES URLS : cleanPhysicianName et physicianNameSlug
 * (url-builder.ts) restent intouchés, donc les slugs canoniques et
 * physician-slug-redirects.json ne bougent pas.
 *
 * Étendre = ajouter des lignes relues ici, jamais brancher l'heuristique.
 */
const PHYSICIAN_DISPLAY_OVERRIDES = physicianDisplayOverridesRaw as Record<
  string,
  { firstName: string; lastName: string }
>;

export type Localizable = {
  nameFr: string;
  nameAr?: string;
  nameEn?: string;
};

export type PhysicianNameSource = {
  /** Slug de la fiche — clé de PHYSICIAN_DISPLAY_OVERRIDES. Optionnel : les
   *  appelants qui ne le passent pas (MapView, suggestions) gardent le
   *  comportement actuel. */
  slug?: string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  firstNameAr?: string | null;
  lastNameAr?: string | null;
  firstNameEn?: string | null;
  lastNameEn?: string | null;
  isCenter?: boolean;
  /** Raison sociale complète (firstName + lastName) quand isCenter. */
  centerName?: string | null;
};

// ═══════════════════ Entity pattern sanitizer ═══════════════════
//
// Certaines fiches ont un lastName FR pollué par un nom de cabinet/centre
// concaténé après une virgule ou un séparateur ("Safaa , cabinet d'Hépato…").
// cleanLastNameForDisplay strip ces patterns au runtime sans muter la DB.
// Apostrophes acceptées dans "d'" : U+0027 (typed) + U+2019 (typographique).
// Suffix \s+.+ optionnel pour matcher aussi "cabinet" seul ou "hôpital Ibn Sina"
// (sans article).
const ENTITY_PATTERNS = [
  /[,;\-–—]\s*cabinet(\s+.+)?$/i,
  /[,;\-–—]\s*centre(\s+.+)?$/i,
  /[,;\-–—]\s*clinique(\s+.+)?$/i,
  /[,;\-–—]\s*laboratoire(\s+.+)?$/i,
  /[,;\-–—]\s*maternit[ée](\s+.+)?$/i,
  /[,;\-–—]\s*polyclinique(\s+.+)?$/i,
  /[,;\-–—]\s*h[ôo]pital(\s+.+)?$/i,
];

/**
 * Strip les patterns d'entité business du lastName pour l'affichage.
 * Ex: "Safaa , cabinet d'Hépato-Gastro-Entérologie" → "Safaa"
 * Conserve la DB canonical, ne fait que nettoyer le display.
 *
 * Couvre cabinet / centre / clinique / laboratoire / maternité / polyclinique / hôpital
 * après [, ; - – —], avec article optionnel (de / d' / du).
 *
 * Unit tests inline:
 * - cleanLastNameForDisplay("Safaa , cabinet d'Hépato-Gastro-Entérologie") === "Safaa"
 * - cleanLastNameForDisplay("BENNANI") === "BENNANI"
 * - cleanLastNameForDisplay("Marie-Claire, centre de radiologie") === "Marie-Claire"
 * - cleanLastNameForDisplay("Smith") === "Smith"
 */
export function cleanLastNameForDisplay(lastName: string | null | undefined): string {
  if (!lastName) return "";
  const original = lastName.trim();
  let cleaned = original;
  for (const re of ENTITY_PATTERNS) {
    cleaned = cleaned.replace(re, "");
  }
  cleaned = cleaned.trim().replace(/[,;\-–—\s]+$/g, "").trim();
  // Guard : si le clean ramene une string vide (cas ou tout le lastName etait
  // un pattern entity, ex: "Cabinet Dentaire Multidisciplinaire"), garder
  // l'original pour ne JAMAIS retourner "" sur un input non-vide. Sinon le
  // breadcrumb position 4 a un name vide (incident GSC Breadcrumbs 2026-06-13).
  return cleaned || original;
}

/**
 * Renvoie {firstName, lastName} dans la locale demandée si traduit, fallback latin sinon.
 * Crucial pour H1, meta title, JSON-LD, og:image alt sur /ar/medecin/*.
 * Le fallback latin passe par cleanLastNameForDisplay pour neutraliser les
 * lastName pollués par un nom de cabinet ("Safaa , cabinet d'…" → "Safaa").
 */
export function getLocalizedPhysicianName(
  doc: PhysicianNameSource,
  locale: string,
): { firstName: string; lastName: string } {
  if (locale === "ar" && doc.firstNameAr && doc.lastNameAr) {
    return { firstName: doc.firstNameAr, lastName: doc.lastNameAr };
  }
  if (locale === "en" && doc.firstNameEn && doc.lastNameEn) {
    return { firstName: doc.firstNameEn, lastName: doc.lastNameEn };
  }
  return {
    firstName: doc.firstName ?? "",
    lastName: cleanLastNameForDisplay(doc.lastName),
  };
}

/**
 * Décode les entités HTML communes laissées par le scraper (Dabadoc/med.ma)
 * dans les noms médecin/clinique : "Dr Art&#39;s Clinic" → "Dr Art's Clinic".
 * Appelé au rendu (dans formatDoctorName, localizedName) pour ne pas avoir
 * à muter la base scraped.
 */
export function unescapeHtml(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, "/");
}

export type Locale = "fr" | "ar" | "en";

/** Returns localized name with FR fallback + HTML entities decoded. */
export function localizedName<T extends Localizable>(item: T, locale: Locale | string): string {
  if (locale === "ar" && item.nameAr) return unescapeHtml(item.nameAr);
  if (locale === "en" && item.nameEn) return unescapeHtml(item.nameEn);
  return unescapeHtml(item.nameFr);
}

/** Picks a locale-keyed string field (fooFr/fooAr/fooEn) with FR fallback. */
export function localizedField<K extends string>(
  item: Partial<Record<`${K}Fr` | `${K}Ar` | `${K}En`, string | undefined>>,
  field: K,
  locale: Locale | string,
): string {
  const arField = `${field}Ar` as const;
  const enField = `${field}En` as const;
  const frField = `${field}Fr` as const;
  if (locale === "ar" && item[arField]) return item[arField] as string;
  if (locale === "en" && item[enField]) return item[enField] as string;
  return (item[frField] ?? "") as string;
}

// ═══════════════════ Doctor title localization ═══════════════════
//
// Database stores ~44 000 doctor records with French titles ("Dr", "Pr", "Dre"…).
// Names themselves remain in original Latin script (translating each is out of
// scope); only the prefix is localized per locale.

// Format Doctolib-style : "Dr Prénom NOM" (sans point après Dr, NOM en MAJ).
// Sources scraper hétérogènes : "Dr", "Dr.", "Dre", "Dre.", "Pr", "Pr." → on
// normalise vers le préfixe canonique sans point.
//
// Civilités : tous les praticiens de l'annuaire ne sont pas médecins
// (hypnothérapeutes, kinés, orthophonistes…). Leur titre doit rester civil —
// afficher « Dr » sur un non-médecin serait faux. Le fallback générique
// (retrait du point final) convient à « Mme » et « Mlle », qui s'abrègent
// sans point, mais PAS à « M. » : l'abréviation de Monsieur garde le sien,
// et le rendu était « M Mostafa BOUZIDI IDRISSI ». En AR et EN, le fallback
// laissait passer l'abréviation française telle quelle.
const TITLE_AR_MAP: Record<string, string> = {
  Dr: "د.",
  "Dr.": "د.",
  Dre: "د.",
  "Dre.": "د.",
  Pr: "أ.د.",
  "Pr.": "أ.د.",
  M: "السيد",
  "M.": "السيد",
  Mme: "السيدة",
  "Mme.": "السيدة",
  Mlle: "الآنسة",
  "Mlle.": "الآنسة",
};

const TITLE_EN_MAP: Record<string, string> = {
  Dr: "Dr",
  "Dr.": "Dr",
  Dre: "Dr",
  "Dre.": "Dr",
  Pr: "Prof",
  "Pr.": "Prof",
  M: "Mr",
  "M.": "Mr",
  Mme: "Mrs",
  "Mme.": "Mrs",
  Mlle: "Ms",
  "Mlle.": "Ms",
};

const TITLE_FR_MAP: Record<string, string> = {
  Dr: "Dr",
  "Dr.": "Dr",
  Dre: "Dr",
  "Dre.": "Dr",
  Pr: "Pr",
  "Pr.": "Pr",
  M: "M.",
  "M.": "M.",
  Mme: "Mme",
  "Mme.": "Mme",
  Mlle: "Mlle",
  "Mlle.": "Mlle",
};

/** Returns the localized doctor title prefix (FR Dr/Pr sans point, AR → د., EN → Dr/Prof). */
/** Civilité d'un praticien non médecin (M., Mme, Mlle — kiné, psychologue, hypnothérapeute…),
 *  choisie dans l'éditeur de profil Pro. Un titre vide n'en est PAS une : 466 dentistes et
 *  79 généralistes ont un titre vide en base (13/10/2026). */
export function isNonMedicalTitle(title: string | null | undefined): boolean {
  return /^(m|mme|mlle|mr|mrs|ms)\.?$/i.test((title ?? "").trim());
}

/** Spécialités sans inscription établie à l'Ordre des médecins : profession non médicale, ou
 *  exercice ouvert aux non-médecins. Le titre en base ne tranche pas : « Dr » vient des imports
 *  et le portaient 68 opticiens et 322 kinés (audit 2026-11-01). */
const NON_PHYSICIAN_SPECIALTIES = new Set([
  "acupuncteur", "audioprothesiste", "biologiste", "chiropracteur", "hypnotherapeute",
  "infirmier", "kinesitherapeute", "medecine-alternative", "naturopathe", "nutritionniste",
  "opticien", "orthophoniste", "orthoptiste", "osteopathe", "pharmacien", "podologue",
  "psychologue", "psychomotricien", "sage-femme",
]);
/** Les dentistes relèvent de leur propre Ordre, pas de l'Ordre des médecins. */
const DENTAL_SPECIALTIES = new Set(["chirurgien-dentiste", "dentiste", "orthodontiste"]);

export type PractitionerKind = "physician" | "dentist" | "other";

export function practitionerKind(
  title: string | null | undefined,
  specialtySlug: string | null | undefined,
): PractitionerKind {
  if (isNonMedicalTitle(title) || NON_PHYSICIAN_SPECIALTIES.has(specialtySlug ?? "")) return "other";
  return DENTAL_SPECIALTIES.has(specialtySlug ?? "") ? "dentist" : "physician";
}

export function localizedTitle(title: string, locale: Locale | string): string {
  if (locale === "ar") return TITLE_AR_MAP[title] ?? title;
  if (locale === "en") return TITLE_EN_MAP[title] ?? title;
  return TITLE_FR_MAP[title] ?? title.replace(/\.$/, "");
}

/** Met le NOM en MAJUSCULES (Doctolib-style) en gérant les caractères accentués
 * et les particules ("de", "du", "le", "la", "el", "al"...) qui restent en
 * minuscules : "El Idrissi" → "EL IDRISSI", "de la Cruz" → "DE LA CRUZ".
 * Pour les noms composés avec tiret on capitalise chaque partie. */
export function uppercaseLastName(s: string): string {
  return s.toLocaleUpperCase("fr-FR");
}

/** Format full doctor name "Dr Prénom NOM" (Doctolib-style) avec :
 *  - préfixe canonique "Dr" (sans point) en FR, "د." en AR, "Dr"/"Prof" en EN
 *  - prénom/nom localisés via getLocalizedPhysicianName (firstNameAr/En si dispo, fallback latin)
 *  - prénom title-case (préservé tel quel depuis DB normalisée)
 *  - NOM de famille en MAJUSCULES (locale-aware : fr-FR / en-US / ar)
 *  - cleanPhysicianName : strip parasites scraper (lastName="Psychiatrie Dr X" → "X")
 * Pour les centres (isCenter=true) on retourne juste le lastName localisé tel quel
 * (le nom du centre n'est pas un titre médical, on garde le nom commercial intact). */
export function formatDoctorName(
  doctor: PhysicianNameSource,
  locale: Locale | string,
): string {
  const localized = getLocalizedPhysicianName(doctor, locale);
  const fnRaw = unescapeHtml(localized.firstName);
  const lnRaw = unescapeHtml(localized.lastName);
  // Centres : raison sociale nettoyée de ses segments « Dentiste Rabat » /
  // « Implant dentaire I Facette dentaire » (295 fiches, cf. center-name.ts),
  // dans la graphie de la langue de la page quand le nom est bilingue.
  if (doctor.isCenter) return cleanCenterDisplayName(unescapeHtml(doctor.centerName?.trim() || lnRaw), locale);
  // Strip parasites (audit 2026-05-11 : ~2 400 physicians avec firstName="de"
  // ou lastName="Spécialité Dr X Y" = scraper pollution).
  const cleaned = cleanPhysicianName(fnRaw, lnRaw);
  // Liste blanche relue (cf. PHYSICIAN_DISPLAY_OVERRIDES) — lookup, pas
  // heuristique. Uniquement sur la graphie latine : quand la fiche a un nom
  // AR/EN propre, c'est lui qui est affiché et l'override latin n'a rien à y
  // faire.
  const usesLocalizedName =
    (locale === "ar" && !!doctor.firstNameAr && !!doctor.lastNameAr) ||
    (locale === "en" && !!doctor.firstNameEn && !!doctor.lastNameEn);
  const override =
    !usesLocalizedName && doctor.slug
      ? PHYSICIAN_DISPLAY_OVERRIDES[doctor.slug]
      : undefined;
  const fn = override ? override.firstName : cleaned.firstName || fnRaw;
  const ln = override ? override.lastName : cleaned.lastName || lnRaw;
  const upperLocale = locale === "ar" ? "ar" : locale === "en" ? "en-US" : "fr-FR";
  const upperLn = ln.toLocaleUpperCase(upperLocale);
  const prefix = localizedTitle(doctor.title ?? "Dr", locale);
  // Collapse des espaces : un override peut n'avoir qu'un prénom (« Dr Rime »)
  // ou qu'un nom (« Dr JOUID »), sinon on rendait « Dr  JOUID ».
  return `${prefix} ${fn} ${upperLn}`.replace(/\s+/g, " ").trim();
}
